import type { CalendarDate } from "../../dateAdapter";
import { monthsShort } from "../../dateAdapter";
import { includes, isArray, isNil, range, some, uniq } from "../../lib/utils";
import { MONTHS_IN_YEAR } from "./const";

const buildCalendarValues = (localization?: string): string[] => {
  /*
    Return array of months (strings) like ['Aug', 'Sep', ...]
    that used to populate calendar's page.
  */
  return monthsShort(localization);
};

const getInitialDatePosition = (
  selectable: number[],
  currentDate: CalendarDate,
): number | undefined => {
  if (selectable.length === 0) {
    return undefined;
  }
  if (selectable.indexOf(currentDate.month()) < 0) {
    return selectable[0];
  }

  return currentDate.month();
};

const getDisabledPositions = (
  enable: CalendarDate[] | undefined,
  disable: CalendarDate[] | undefined,
  maxDate: CalendarDate | undefined,
  minDate: CalendarDate | undefined,
  currentDate: CalendarDate,
): number[] | undefined => {
  /*
    Return position numbers of months that should be displayed as disabled
    (position in array returned by `this.buildCalendarValues`).
  */
  let disabled: number[] = [];
  if (isArray(enable)) {
    const enabledMonthPositions = enable
      .filter((monthMoment) => monthMoment.isSame(currentDate, "year"))
      .map((monthMoment) => monthMoment.month());
    disabled = disabled.concat(
      range(0, MONTHS_IN_YEAR).filter(
        (monthPosition) => !includes(enabledMonthPositions, monthPosition),
      ),
    );
  }
  if (isArray(disable)) {
    disabled = disabled.concat(
      disable
        .filter((monthMoment) => monthMoment.year() === currentDate.year())
        .map((monthMoment) => monthMoment.month()),
    );
  }
  if (!isNil(maxDate)) {
    if (maxDate.year() === currentDate.year()) {
      disabled = disabled.concat(range(maxDate.month() + 1, MONTHS_IN_YEAR));
    }
    if (maxDate.year() < currentDate.year()) {
      disabled = range(0, MONTHS_IN_YEAR);
    }
  }
  if (!isNil(minDate)) {
    if (minDate.year() === currentDate.year()) {
      disabled = disabled.concat(range(0, minDate.month()));
    }
    if (minDate.year() > currentDate.year()) {
      disabled = range(0, MONTHS_IN_YEAR);
    }
  }
  if (disabled.length > 0) {
    return uniq(disabled);
  }
};

const isNextPageAvailable = (
  maxDate: CalendarDate | undefined,
  enable: CalendarDate[] | undefined,
  currentDate: CalendarDate,
): boolean => {
  if (isArray(enable)) {
    return some(enable, (enabledMonth) =>
      enabledMonth.isAfter(currentDate, "year"),
    );
  }
  if (isNil(maxDate)) {
    return true;
  }

  return currentDate.year() < maxDate.year();
};

const isPrevPageAvailable = (
  minDate: CalendarDate | undefined,
  enable: CalendarDate[] | undefined,
  currentDate: CalendarDate,
): boolean => {
  if (isArray(enable)) {
    return some(enable, (enabledMonth) =>
      enabledMonth.isBefore(currentDate, "year"),
    );
  }
  if (isNil(minDate)) {
    return true;
  }

  return currentDate.year() > minDate.year();
};

export {
  buildCalendarValues,
  getInitialDatePosition,
  getDisabledPositions,
  isNextPageAvailable,
  isPrevPageAvailable,
};
